import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MoodBoard } from '../components/moodboard/MoodBoard';
import { HistoryDashboard } from '../components/HistoryDashboard';
import { MeetingFeedbackModal } from '../components/MeetingFeedbackModal';
import api from '../services/api';
import { User, Meeting, Team } from '../types';
import { Calendar } from 'lucide-react';

interface DashboardProps {
    currentUser: User;
}

export const Dashboard: React.FC<DashboardProps> = ({ currentUser }) => {
    const [meetings, setMeetings] = useState<Meeting[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
    const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
    const [sessionId, setSessionId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);

    // Fetch User's Meetings and Teams
    useEffect(() => {
        const initDashboard = async () => {
            try {
                // Fetch meetings (assuming /meetings returns all accessible meetings)
                const meetingsRes = await api.get('/meetings/');
                const userMeetings: Meeting[] = meetingsRes.data;
                setMeetings(userMeetings);

                // Derive Teams (or fetch if needed, but we can group by team_id)
                // For MVP, we might need to fetch teams to get names if they aren't expanded in meeting
                // Let's assume we can fetch teams separately
                const teamsRes = await api.get('/teams/my-teams').catch(() => ({ data: [] })); // Fallback if endpoint doesn't exist yet
                // Alternatively, just extract unique team IDs and names if available
                // If /teams/my-teams doesn't exist, we might need to rely on what's available.
                // Re-checking teams.py... /teams/my-teams isn't explicitly there, 
                // but /teams usually returns all for admin. For user?
                // Use /teams for now, assuming user can see their teams? 
                // Or just Filter meetings. 

                // Let's implement a safe fallback: Extract unique team IDs from meetings
                if (teamsRes.data.length > 0) {
                    setTeams(teamsRes.data);
                } else {
                    // Can't easily get names without endpoint.
                    // Let's rely on meetings for now. 
                }

                if (userMeetings.length > 0) {
                    // Default to first meeting
                    setSelectedMeetingId(userMeetings[0].id);
                    setSelectedTeamId(userMeetings[0].team_id);
                }
            } catch (err) {
                console.error("Failed to load dashboard", err);
            } finally {
                setLoading(false);
            }
        };
        initDashboard();
    }, []);

    // Fetch Session when Meeting Changes
    useEffect(() => {
        if (!selectedMeetingId) return;

        const fetchSession = async () => {
            try {
                const res = await api.get(`/meetings/${selectedMeetingId}/sessions`);
                const sessions = res.data;
                if (sessions.length > 0) {
                    const latestSession = sessions[0];
                    const sessionDate = new Date(latestSession.date);
                    const now = new Date();

                    // Compare dates in UTC to match backend behavior
                    // Sessions are stored in UTC, so we compare UTC date strings
                    const sessionDateUTC = sessionDate.toISOString().split('T')[0];
                    const todayUTC = now.toISOString().split('T')[0];
                    const isToday = sessionDateUTC === todayUTC;

                    if (isToday) {
                        // Session exists for today - show MoodBoard
                        setSessionId(latestSession.id);
                    } else {
                        // Session is from past - show "Start Today's Session"
                        setSessionId(null);
                    }
                } else {
                    setSessionId(null);
                }
            } catch (err) {
                console.error("Failed to fetch session", err);
            }
        };
        fetchSession();
    }, [selectedMeetingId]);


    if (loading) return <div className="flex justify-center p-8">Loading Dashboard...</div>;

    if (meetings.length === 0) {
        return (
            <div className="text-center p-8 text-gray-500">
                <h2>No meetings assigned. Ask your admin to add you to a team.</h2>
            </div>
        );
    }

    const currentMeeting = meetings.find(m => m.id === selectedMeetingId);

    return (
        <div className="space-y-12">
            {/* Context Selectors */}
            {(meetings.length > 1) && (
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex gap-4 items-center">
                    <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">Context:</span>

                    {/* Meeting Selector */}
                    <select
                        value={selectedMeetingId || ''}
                        onChange={(e) => setSelectedMeetingId(Number(e.target.value))}
                        className="px-4 py-2 border rounded-lg bg-gray-50 font-medium text-gray-800 focus:ring-2 focus:ring-purple-500 outline-none"
                    >
                        {meetings.map(m => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                    </select>

                    <Link
                        to={`/history/${selectedMeetingId}`}
                        className="ml-auto flex items-center gap-2 px-4 py-2 text-sm font-bold text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                        <Calendar className="w-4 h-4" />
                        History View
                    </Link>
                </div>
            )}

            <section>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">
                        {currentMeeting?.name || 'Mood Board'}
                    </h2>
                    <div className="text-sm text-gray-500">
                        Session ID: {sessionId || '...'}
                    </div>
                </div>

                {sessionId ? (
                    <MoodBoard
                        sessionId={sessionId}
                        currentUser={currentUser}
                        onConfirm={() => console.log("Confirmed")}
                    />
                ) : (
                    <div className="p-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
                        <p className="text-gray-500 mb-4">No session for today yet. Start a new session to collect feedback.</p>
                        {/* Any team member can start today's session */}
                        <button
                            onClick={async () => {
                                if (!selectedMeetingId) return;
                                setLoading(true);
                                try {
                                    const res = await api.post(`/sessions/start-today/${selectedMeetingId}`);
                                    setSessionId(res.data.id);
                                } catch (err: any) {
                                    console.error("Failed to start session", err);
                                    const errorMsg = err.response?.data?.detail || "Failed to start session";
                                    alert(errorMsg);
                                } finally {
                                    setLoading(false);
                                }
                            }}
                            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm font-medium"
                        >
                            Start Today's Session
                        </button>
                    </div>
                )}
            </section>

            {selectedMeetingId && (
                <section>
                    <HistoryDashboard meetingId={selectedMeetingId} currentUser={currentUser} />
                </section>
            )}
        </div>
    );
};

